'use client'

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import clsx from 'clsx'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'

import {
  Brand,
  getSelectValueBySortingOptions,
  getSortingOptionsBySelectValue,
  SORTING_OPTIONS,
  SortingOptionsTypes,
  useFilterChange
} from 'modules/products'
import Button from 'shared/ui/Button'
import SelectDropdown from 'shared/ui/form/Select'
import { searchParamsToParsedUrlQuery } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { FilterDictionary } from '../types'

import PriceFilter from './PriceFilter'
import ProducerFilter from './ProducerFilter'
import TagsFilter from './TagsFilter'
import WeeklyAvailabilityFilter from './WeeklyAvailabilityFilter'

import styles from './Filters.module.scss'

interface FiltersProps {
  brands: Brand[]
  tags: FilterDictionary[]
  deliveryDates: string[]
}

type FiltersState = {
  tags: boolean
  price: boolean
  brands: boolean
  deliveryDates: boolean
}

type Action =
  | { type: 'SET_ACTIVE_FILTER'; filter: keyof FiltersState; active: boolean }
  | { type: 'RESET_FILTERS' }

const filtersReducer = (state: FiltersState, action: Action): FiltersState => {
  switch (action.type) {
    case 'SET_ACTIVE_FILTER':
      return { ...state, [action.filter]: action.active }
    case 'RESET_FILTERS':
      return {
        tags: false,
        price: false,
        brands: false,
        deliveryDates: false
      }
    default:
      return state
  }
}

const Filters = ({ brands, tags, deliveryDates }: FiltersProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  const [filtersState, dispatch] = useReducer(filtersReducer, {
    tags: false,
    price: false,
    brands: false,
    deliveryDates: false
  })
  const [sorting, setSorting] = useState<string | string[]>('recommended')

  useEffect(() => {
    if (!searchParams) return
    const parsedQuery = qs.parse(searchParams.toString())

    const filter =
      (parsedQuery.filter as {
        tag?: unknown
        price?: unknown
        brand_id?: unknown
        daily_availability_plan?: unknown
      }) || {}

    dispatch({
      type: 'SET_ACTIVE_FILTER',
      filter: 'tags',
      active: isNotNilOrEmpty(filter.tag)
    })
    dispatch({
      type: 'SET_ACTIVE_FILTER',
      filter: 'price',
      active: isNotNilOrEmpty(filter.price)
    })
    dispatch({
      type: 'SET_ACTIVE_FILTER',
      filter: 'brands',
      active: isNotNilOrEmpty(filter.brand_id)
    })
    dispatch({
      type: 'SET_ACTIVE_FILTER',
      filter: 'deliveryDates',
      active: isNotNilOrEmpty(filter.daily_availability_plan)
    })
  }, [searchParams])

  // check sorting option in url
  useEffect(() => {
    const parsedQuery = searchParamsToParsedUrlQuery(searchParams)
    const sortingToCheck = {
      sort_by: parsedQuery.sort_by,
      order: parsedQuery.order
    } as SortingOptionsTypes

    const sortingName = getSelectValueBySortingOptions(sortingToCheck)

    if (sortingName !== sorting) {
      setSorting(sortingName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleSortingChange = (_: unknown, value: string | string[]) => {
    setLastFilterChange(new Date())
    setSorting(value)

    const sortingOptions = getSortingOptionsBySelectValue(value)

    const currentQuery = searchParamsToParsedUrlQuery(searchParams)

    // @sieradz - if they are recommended then we don't sort
    // (from the API we get a ready list), so we remove
    // from the query - rest order and sort_by
    if (value === 'recommended') {
      delete currentQuery?.order
      delete currentQuery?.sort_by
    }

    const params = qs.stringify(
      { ...currentQuery, ...sortingOptions },
      { addQueryPrefix: true }
    )

    router.replace(`${pathname || ''}${decodeURIComponent(params)}`)
  }

  const handleClearFilters = useCallback(() => {
    setLastFilterChange(new Date())
    router.replace(pathname || '')
  }, [pathname, router, setLastFilterChange])

  const hasFilters = useMemo(
    () => Object.values(filtersState).some(Boolean),
    [filtersState]
  )

  return (
    <Wrapper>
      <div className={styles.filtersWrapper}>
        <TagsFilter isActive={filtersState.tags} tags={tags} />
        <PriceFilter isActive={filtersState.price} />
        <ProducerFilter isActive={filtersState.brands} brands={brands} />
        <WeeklyAvailabilityFilter
          isActive={filtersState.deliveryDates}
          deliveryDates={deliveryDates}
        />
        {hasFilters && (
          <Button
            size='small'
            variant='primaryText'
            onClick={handleClearFilters}
          >
            Wyczyść filtry
          </Button>
        )}
      </div>
      <div className={styles.sortingInputWrapper} {...addTestId('sorting-input')}>
        <div className={styles.sortingLabel}>Sortuj:</div>
        <SelectDropdown
          id='category-page-sorting'
          variant='ghost'
          noMargin
          options={SORTING_OPTIONS}
          name='sorting'
          value={sorting}
          onChange={handleSortingChange}
        />
      </div>
    </Wrapper>
  )
}

export default Filters

const Wrapper = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={clsx(styles.wrapper, className)} />
)
